import {
  STANDARD_POINTS,
  TournamentStore,
  engineIds,
  inferInitialColour,
  parseDescriptor,
  parseTrf,
  pointSystemFromTrf,
  recordedPairing,
  roundsInTrf,
  toTrf,
  writeTrf,
  type Game,
  type PairingSystemId,
  type RoundBye,
  type RoundEntry,
  type Title,
  type Tournament,
  type TournamentEvent,
  type TournamentEventBody,
  type TournamentSettings,
} from '@ctd/core';
import { newId } from './ids';
import { LOCAL_ACTOR } from './session';

/** The native file: the whole event log (the same format as the CLI's tournament file). */
export interface TournamentFile {
  readonly format: 'ctd-events';
  readonly version: 1;
  readonly events: readonly TournamentEvent[];
}

export function toTournamentFile(events: readonly TournamentEvent[]): TournamentFile {
  return { format: 'ctd-events', version: 1, events };
}

export function exportJson(events: readonly TournamentEvent[]): string {
  return JSON.stringify(toTournamentFile(events), null, 1) + '\n';
}

/** The tournament as a TRF26 file (FIDE's current report and pairing-input format). */
export function exportTrf(t: Tournament): string {
  return writeTrf(toTrf(t));
}

/** A file name from the tournament name: "Club Open 2026" → "club-open-2026". */
export function fileBaseName(name: string): string {
  const base = name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return base === '' ? 'tournament' : base.slice(0, 60);
}

/** Read and check a native file; the events are replayed to make sure they apply. */
export function parseTournamentFile(text: string): TournamentEvent[] {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error('the file is not valid JSON');
  }
  const f = data as Partial<TournamentFile> | null;
  if (f === null || typeof f !== 'object' || f.format !== 'ctd-events') {
    throw new Error('the file is not a Chess TD tournament file (format "ctd-events")');
  }
  if (f.version !== 1) throw new Error(`unsupported file version ${String(f.version)}`);
  if (!Array.isArray(f.events) || f.events.length === 0) {
    throw new Error('the file holds no events');
  }
  const events = f.events as TournamentEvent[];
  if (events[0]?.type !== 'TournamentCreated') {
    throw new Error('the first event must be TournamentCreated');
  }
  try {
    new TournamentStore(events);
  } catch (e) {
    throw new Error(`the event log does not replay: ${e instanceof Error ? e.message : String(e)}`);
  }
  return events;
}

/**
 * Give an event log a new tournament id (the id lives in the creation event),
 * for duplicates and for imports that would overwrite an existing tournament.
 */
export function withNewId(events: readonly TournamentEvent[], id = newId('t')): TournamentEvent[] {
  return events.map((e, i) => (i === 0 && e.type === 'TournamentCreated' ? { ...e, id } : e));
}

/** A copy of a tournament under a new id, with a logged rename to "<name> (copy)". */
export function duplicateEvents(events: readonly TournamentEvent[]): TournamentEvent[] {
  const store = new TournamentStore(withNewId(events));
  store.dispatch(
    { type: 'SettingsChanged', patch: { name: `${store.state.settings.name} (copy)` } },
    LOCAL_ACTOR,
  );
  return [...store.events];
}

export interface TrfImport {
  readonly events: TournamentEvent[];
  readonly warnings: string[];
}

function systemFromTrfType(type: string | null): PairingSystemId {
  const available = engineIds();
  const t = (type ?? '').toUpperCase();
  let wanted: string;
  if (t.includes('DOUBLEROUNDROBIN')) wanted = 'double-round-robin';
  else if (t.includes('ROUNDROBIN')) wanted = 'round-robin';
  else if (t.includes('DUTCH')) wanted = 'dutch';
  else if (t.includes('BURSTEIN')) wanted = 'burstein';
  else if (t.includes('DUBOV')) wanted = 'dubov';
  else wanted = available.includes('dutch') ? 'dutch' : 'monrad';
  if (available.includes(wanted)) return wanted as PairingSystemId;
  return (available.includes('dutch') ? 'dutch' : 'monrad') as PairingSystemId;
}

const TRF_TITLES: Record<string, Title> = {
  G: 'GM',
  I: 'IM',
  F: 'FM',
  C: 'CM',
  WG: 'WGM',
  WI: 'WIM',
  WF: 'WFM',
  WC: 'WCM',
};
const TITLES = new Set<string>(['GM', 'IM', 'FM', 'CM', 'WGM', 'WIM', 'WFM', 'WCM', 'HM']);

/** TRF titles are written "g", "wg", ... or in full ("GM"). */
export function titleFromTrf(text: string): Title | '' {
  const t = text.trim().toUpperCase();
  if (TITLES.has(t)) return t as Title;
  return TRF_TITLES[t] ?? '';
}

function entryAt(rounds: readonly RoundEntry[], r: number): RoundEntry {
  return rounds[r - 1] ?? { opponent: null, colour: null, result: 'Z' };
}

/**
 * Build a tournament from a TRF file: settings from the header records,
 * players from the 001 lines, and every round in the file (pairings, byes and
 * results) as a RoundPaired event marked engine "trf-import".
 */
export function importTrf(text: string, now = () => new Date().toISOString()): TrfImport {
  const doc = parseTrf(text);
  const warnings: string[] = [];
  if (doc.players.length === 0) throw new Error('the TRF file has no player (001) lines');
  const h = (code: string): string => doc.headers.get(code)?.trim() ?? '';
  const tiebreaks: string[] = [];
  for (const tb of doc.tiebreaks) {
    try {
      tiebreaks.push(parseDescriptor(tb).text);
    } catch {
      warnings.push(`tiebreak ${tb} is not supported yet and was left out`);
    }
  }
  const played = roundsInTrf(doc);
  const settings: TournamentSettings = {
    name: h('012') || 'Imported tournament',
    city: h('022'),
    federation: h('032'),
    startDate: h('042'),
    endDate: h('052'),
    chiefArbiter: h('102'),
    deputyArbiters: h('112') === '' ? [] : h('112').split(/\s*,\s*/),
    timeControl: h('122'),
    rounds: Math.max(doc.totalRounds ?? played, played, 1),
    system: systemFromTrfType(doc.tournamentType),
    pointSystem:
      Object.keys(doc.scoring).length === 0 ? STANDARD_POINTS : pointSystemFromTrf(doc.scoring),
    initialColour: inferInitialColour(doc),
    tiebreaks: tiebreaks.length > 0 ? tiebreaks : ['BH/C1', 'BH', 'SB', 'WIN'],
    acceleration: doc.accelerations.length > 0 ? 'baku' : 'none',
  };
  if (doc.accelerations.length > 0) {
    warnings.push('the file has acceleration lines, so the tournament uses Baku acceleration');
  }
  const store = new TournamentStore([], now);
  store.dispatch({ type: 'TournamentCreated', id: newId('t'), settings }, 'TRF import');
  const players = [...doc.players].sort((a, b) => a.startRank - b.startRank);
  const idByRank = new Map<number, string>();
  for (const p of players) {
    const id = newId('p');
    idByRank.set(p.startRank, id);
    store.dispatch(
      {
        type: 'PlayerAdded',
        player: {
          id,
          name: p.name,
          rating: p.rating,
          ...(p.rating > 0 ? { fideRating: p.rating } : {}),
          title: titleFromTrf(p.title),
          federation: p.federation,
          fideId: p.fideId,
          birthDate: p.birthDate,
          sex: p.sex,
          withdrawnAfterRound: null,
          byeRequests: [],
        },
      },
      'TRF import',
    );
  }
  for (let r = 1; r <= played; r++) {
    const body = trfRound(players, idByRank, r, recordedPairing(doc, r));
    try {
      store.dispatch(body, 'TRF import');
    } catch (e) {
      warnings.push(
        `round ${r} could not be read (${e instanceof Error ? e.message : String(e)}); ` +
          'this and later rounds were left out',
      );
      break;
    }
  }
  return { events: [...store.events], warnings };
}

function trfRound(
  players: readonly { startRank: number; rounds: readonly RoundEntry[] }[],
  idByRank: ReadonlyMap<number, string>,
  round: number,
  rec: ReturnType<typeof recordedPairing>,
): TournamentEventBody {
  const id = (rank: number): string => {
    const v = idByRank.get(rank);
    if (v === undefined) throw new Error(`round ${round} names unknown player ${rank}`);
    return v;
  };
  const byRank = new Map(players.map((p) => [p.startRank, p]));
  const games: Game[] = rec.pairs.map((pair, i) => {
    const w = entryAt(byRank.get(pair.white)?.rounds ?? [], round);
    const b = entryAt(byRank.get(pair.black)?.rounds ?? [], round);
    return {
      board: i + 1,
      white: id(pair.white),
      black: id(pair.black),
      result: { white: w.result, black: b.result },
    };
  });
  const inGame = new Set(rec.pairs.flatMap((p) => [p.white, p.black]));
  const byes: RoundBye[] = [];
  for (const p of players) {
    if (inGame.has(p.startRank)) continue;
    const e = entryAt(p.rounds, round);
    const kind = e.result === 'U' || e.result === 'F' || e.result === 'H' ? e.result : 'Z';
    byes.push({ player: id(p.startRank), kind });
  }
  return { type: 'RoundPaired', round, games, byes, engine: 'trf-import' };
}

/** Is this text a TRF file rather than JSON? */
export function looksLikeTrf(text: string): boolean {
  const t = text.trimStart();
  return !t.startsWith('{') && /^(001|012|XXR|142)\s/m.test(text);
}
